import React, { useState } from "react";

const LightPuzzleGame = () => {
  // 初始状态:只有2号灯(索引1)和6号灯(索引5)是开着的
  const initialState = [false, true, false, false, false, true, false];
  const [lights, setLights] = useState([...initialState]);
  const [mode, setMode] = useState(3); // 3个灯或5个灯模式
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [moves, setMoves] = useState(0);
  const [history, setHistory] = useState([]);
  const [showXOR, setShowXOR] = useState(false); // 是否显示异或运算
  const [showXORDetails, setShowXORDetails] = useState(true); // 是否显示详细步骤

  // 检查是否所有灯都亮着
  const isComplete = lights.every((light) => light);

  // 重置游戏
  const resetGame = () => {
    setLights([...initialState]);
    setMoves(0);
    setHistory([]);
    setSelectedPosition(null);
  };

  // 获取相邻的灯的索引
  const getAdjacentIndices = (position, count) => {
    const indices = [];
    const halfCount = Math.floor(count / 2);

    for (let i = -halfCount; i <= halfCount; i++) {
      let index = (position + i + 7) % 7;
      indices.push(index);
    }
    return indices;
  };

  // 切换指定位置的灯组
  const toggleLights = (position) => {
    const newLights = [...lights];
    const indicesToToggle = getAdjacentIndices(position, mode);

    // 创建操作向量（记录哪些灯被影响,用于异或显示）
    const operationVector = Array(7).fill(0);
    indicesToToggle.forEach((index) => {
      newLights[index] = !newLights[index];
      operationVector[index] = 1;
    });

    setLights(newLights);
    setMoves(moves + 1);
    setHistory([
      ...history,
      { 
        position: position + 1, 
        mode, 
        state: [...newLights], 
        operation: operationVector 
      },
    ]);
    setSelectedPosition(null);
  };

  // 显示预览效果
  const showPreview = (position) => {
    setSelectedPosition(position);
  };

  // 撤销上一步
  const undoMove = () => {
    if (history.length > 0) {
      const newHistory = [...history];
      newHistory.pop();

      if (newHistory.length > 0) {
        setLights([...newHistory[newHistory.length - 1].state]);
      } else {
        setLights([...initialState]);
      }

      setHistory(newHistory);
      setMoves(Math.max(0, moves - 1));
    }
  };

  // 计算异或结果（奇数个1为1,偶数个1为0）
  const calculateXorResult = () => {
    const result = Array(7).fill(0);
    
    // 先加上初始状态
    initialState.forEach((val, index) => {
      result[index] += val ? 1 : 0;
    });
    
    // 再加上所有操作
    history.forEach(move => {
      move.operation.forEach((val, index) => {
        result[index] += val;
      });
    });
    
    // 奇数个1为1,偶数个1为0（异或的本质）
    return result.map(count => count % 2);
  };

  // 灯泡组件 - 改进的预览效果
  const Light = ({ isOn, position, isAffected }) => {
    return (
      <div 
        className="relative"
        onMouseEnter={() => showPreview(position)}
        onMouseLeave={() => setSelectedPosition(null)}
      >
        <div
          className={`w-12 h-12 rounded-full border-4 transition-all duration-200 cursor-pointer relative overflow-hidden
            ${
              isOn
                ? "bg-yellow-400 border-yellow-500 shadow-lg shadow-yellow-300"
                : "bg-gray-300 border-gray-400"
            }
            hover:scale-110 transform
          `}
          onClick={() => toggleLights(position)}
        >
          {/* 灯泡高光效果 */}
          <div className="absolute inset-2 rounded-full bg-white opacity-30"></div>
          
          {/* 预览遮罩层 - 半透明彩色遮罩 */}
          {isAffected && selectedPosition !== null && (
            <div 
              className="absolute inset-0 rounded-full animate-pulse"
              style={{
                background: isOn 
                  ? 'rgba(59, 130, 246, 0.5)' // 亮灯预览:蓝色半透明
                  : 'rgba(34, 197, 94, 0.6)',  // 暗灯预览:绿色半透明
                boxShadow: isOn
                  ? '0 0 20px rgba(59, 130, 246, 0.6), inset 0 0 10px rgba(255, 255, 255, 0.3)'
                  : '0 0 20px rgba(34, 197, 94, 0.6), inset 0 0 10px rgba(255, 255, 255, 0.3)'
              }}
            />
          )}
          
          {/* 中心点击位置的特殊标记 */}
          {selectedPosition === position && (
            <div className="absolute inset-0 rounded-full border-4 border-white animate-ping" 
                 style={{ animationDuration: '0.5s' }}></div>
          )}
        </div>
        
        {/* 灯编号 - 添加 pointer-events-none 避免干扰鼠标事件 */}
        <div className={`absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-sm font-bold transition-colors pointer-events-none
          ${selectedPosition === position ? 'text-blue-600 scale-125' : 'text-gray-700'}`}>
          {position + 1}
        </div>
      </div>
    );
  };

  // 计算圆形布局的位置
  const getCirclePosition = (index, total, radius = 100) => {
    const angle = (2 * Math.PI * index) / total - Math.PI / 2; // 从顶部开始
    const x = radius * Math.cos(angle);
    const y = radius * Math.sin(angle);
    return { x: x + 150, y: y + 150 }; // 中心点偏移
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Q11-1. 同时控制相邻灯
        </h2>
        <p className="text-gray-600 text-sm mb-4">
          目标:让所有7个灯都亮起来!每次可以选择{mode}个相邻的灯同时切换状态。
        </p>
      </div>

      {/* 控制面板 */}
      <div className="flex justify-center gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">模式:</label>
          <button
            onClick={() => {
              setMode(3);
              resetGame();
            }}
            className={`px-3 py-1 rounded text-sm ${
              mode === 3
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            3个灯
          </button>
          <button
            onClick={() => {
              setMode(5);
              resetGame();
            }}
            className={`px-3 py-1 rounded text-sm ${
              mode === 5
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            5个灯
          </button>
        </div>

        <button
          onClick={resetGame}
          className="px-4 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
        >
          重置
        </button>

        <button
          onClick={undoMove}
          disabled={history.length === 0}
          className="px-4 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 disabled:bg-gray-300"
        >
          撤销
        </button>

        <button
          onClick={() => setShowXOR(!showXOR)}
          className={`px-4 py-1 rounded text-sm ${
            showXOR
              ? "bg-purple-500 text-white"
              : "bg-purple-200 text-purple-700"
          } hover:bg-purple-600 hover:text-white`}
        >
          {showXOR ? "隐藏" : "显示"}异或运算
        </button>
      </div>

      <div className="flex gap-6 justify-center items-start flex-wrap">
        {/* 游戏区域 */}
        <div className="flex-shrink-0" onMouseLeave={() => setSelectedPosition(null)}>
          <div className="relative h-80 mx-auto mb-6" style={{ width: "300px" }}>
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              {/* 绘制圆圈连线 */}
              {lights.map((_, index) => {
                const current = getCirclePosition(index, 7);
                const next = getCirclePosition((index + 1) % 7, 7);
                return (
                  <line
                    key={index}
                    x1={current.x}
                    y1={current.y}
                    x2={next.x}
                    y2={next.y}
                    stroke="#e5e7eb"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                );
              })}
            </svg>

            {/* 灯泡 */}
            {lights.map((isOn, index) => {
              const position = getCirclePosition(index, 7);
              const isAffected =
                selectedPosition !== null &&
                getAdjacentIndices(selectedPosition, mode).includes(index);

              return (
                <div
                  key={index}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
                  style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                  }}
                >
                  <Light isOn={isOn} position={index} isAffected={isAffected} />
                </div>
              );
            })}
          </div>

          {/* 状态信息 */}
          <div className="text-center mb-4">
            <div className="flex justify-center gap-6 text-sm">
              <span>
                步数: <strong>{moves}</strong>
              </span>
              <span>
                亮着的灯: <strong>{lights.filter((l) => l).length}/7</strong>
              </span>
            </div>
          </div>

          {/* 成功提示 */}
          {isComplete && (
            <div className="text-center p-4 bg-green-100 border border-green-300 rounded-lg mb-4">
              <h3 className="text-lg font-bold text-green-800">🎉 恭喜你!</h3>
              <p className="text-green-700">你用了 {moves} 步完成了挑战!</p>
            </div>
          )}
        </div>

        {/* 异或运算面板 */}
        {showXOR && (
          <div className="flex-shrink-0 bg-white p-4 rounded-lg border-2 border-purple-300 shadow-lg" style={{ minWidth: "340px", maxWidth: "400px" }}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-purple-700 flex items-center gap-2">
                <span>⊕</span>
                <span>异或运算（XOR）</span>
              </h4>
              {history.length > 0 && (
                <button
                  onClick={() => setShowXORDetails(!showXORDetails)}
                  className="text-xs px-2 py-1 bg-purple-200 text-purple-700 rounded hover:bg-purple-300 transition-colors"
                >
                  {showXORDetails ? "收起" : "展开"}
                </button>
              )}
            </div>
            
            {history.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                开始操作后,这里会显示异或运算过程
              </p>
            ) : (
              <div className="space-y-2">
                {/* 初始状态 */}
                <div className="flex items-center gap-2 mb-3 pb-3 border-b-2 border-gray-300">
                  <span className="text-gray-700 w-20 text-right text-sm font-medium">
                    初始状态:
                  </span>
                  <div className="flex gap-1">
                    {initialState.map((val, idx) => (
                      <span key={idx} className="w-7 text-center bg-gray-200 px-1 py-1 rounded text-sm font-mono font-medium">
                        {val ? 1 : 0}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 详细步骤（可展开/收起） */}
                {showXORDetails && (
                  <div className="mb-3 pb-3 border-b border-gray-200 bg-gray-50 py-2 rounded">
                    <div className="text-xs text-gray-500 mb-2 font-medium pl-2">详细操作步骤:</div>
                    {history.map((move, index) => (
                      <div key={index} className="flex items-center gap-2 mb-1">
                        <span className="text-gray-500 w-20 text-right text-xs">
                          步骤{index + 1}:
                        </span>
                        <div className="flex gap-1">
                          {move.operation.map((val, idx) => (
                            <span 
                              key={idx} 
                              className={`w-7 text-center px-1 py-1 rounded font-mono text-xs ${
                                val === 1 ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-white text-gray-300'
                              }`}
                            >
                              {val}
                            </span>
                          ))}
                        </div>
                        <span className="text-xs text-gray-400 ml-1">
                          #{move.position}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* 累积操作（所有操作的异或结果） */}
                <div className="flex items-center gap-2 mb-3 pb-3 border-b-2 border-purple-300">
                  <span className="text-purple-700 w-20 text-right text-sm font-medium">
                    累积操作:
                  </span>
                  <div className="flex gap-1">
                    {(() => {
                      // 计算所有操作的累积异或
                      const accumulated = Array(7).fill(0);
                      history.forEach((move) => {
                        move.operation.forEach((val, idx) => {
                          accumulated[idx] ^= val;
                        });
                      });
                      return accumulated.map((val, idx) => (
                        <span 
                          key={idx} 
                          className={`w-7 text-center px-1 py-1 rounded font-mono text-sm font-medium ${
                            val === 1 
                              ? 'bg-purple-200 text-purple-800' 
                              : 'bg-gray-100 text-gray-400'
                          }`}
                        >
                          {val}
                        </span>
                      ));
                    })()}
                  </div>
                </div>

                {/* XOR 符号 */}
                <div className="flex items-center justify-center my-2">
                  <span className="text-2xl text-purple-600 font-bold">⊕</span>
                </div>

                {/* 最终结果 */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t-2 border-green-400 bg-gradient-to-r from-green-50 to-yellow-50 px-0 py-3 rounded-lg">
                  <span className="text-green-700 w-20 text-right font-bold text-sm">最终结果:</span>
                  <div className="flex gap-1">
                    {calculateXorResult().map((val, idx) => (
                      <span 
                        key={idx} 
                        className={`w-7 text-center px-1 py-1 rounded font-bold font-mono text-sm ${
                          val === 1 
                            ? 'bg-yellow-400 text-yellow-900 ring-2 ring-yellow-500 shadow-md' 
                            : 'bg-gray-300 text-gray-600'
                        }`}
                      >
                        {val}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 验证提示 */}
                <div className={`text-xs mt-2 p-2 rounded border ${
                  JSON.stringify(calculateXorResult()) === JSON.stringify(lights.map(l => l ? 1 : 0))
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-blue-50 border-blue-200 text-blue-700'
                }`}>
                  {JSON.stringify(calculateXorResult()) === JSON.stringify(lights.map(l => l ? 1 : 0))
                    ? '✓ 验证: 异或结果与当前灯光状态匹配！'
                    : `当前灯光: [${lights.map(l => l ? 1 : 0).join(', ')}]`
                  }
                </div>

                {/* 说明文字移到最下面 */}
                <div className="mt-3 text-xs text-purple-700 bg-purple-50 p-3 rounded border border-purple-200 space-y-1">
                  <div>
                    <strong>💡 原理:</strong> 每个位置统计操作次数，奇数次为1，偶数次为0
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 操作历史 */}
      {history.length > 0 && !showXOR && (
        <div className="mt-6 p-4 bg-white rounded-lg border max-w-2xl mx-auto">
          <h4 className="font-medium mb-2">操作历史:</h4>
          <div className="text-sm text-gray-600 space-y-1">
            {history.map((move, index) => (
              <div key={index}>
                第{index + 1}步: 在{move.position}号灯位置操作{move.mode}个相邻的灯
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 说明 */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200 max-w-2xl mx-auto">
        <h4 className="font-medium mb-2">游戏说明:</h4>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• 点击任意一个灯,会切换以它为中心的{mode}个相邻灯的状态</li>
          <li>• 鼠标悬停在灯上可以预览操作效果</li>
          <li>• 点击"显示异或运算"可以看到每步操作的数学原理</li>
          <li>• 目标是让所有7个灯都亮起来</li>
        </ul>
      </div>
      
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default LightPuzzleGame;
